// src/backendServices/mediaManagment/api_video.ts

import axios from 'axios';
import { API_URLS } from '../urls';
import { BackendResponse, handleError, extractServerConflictMessage } from '../../dataTypes/backendServicesManagment/types';
import { Video, initializedVideo } from '../../dataTypes/mediaManagment/video_types';
import { DocumentWithState } from '../../dataTypes/documentManagment/types';
import { convertDateToString, timeStringToSeconds } from '../../dataTypes/timeManagment/utils';
import { createUpload } from '../fileManagment/uploadWithStore';
import { deleteDocument } from '../documentManagment/api';


import { getUserAgentInformations   }  from '../../utils/utils_userAgent';
import { getAppInformations         }  from '../../utils/utils_app';


// ===== FONCTIONS UTILITAIRES POUR LES DOCUMENTS =====

// L'upload des documents attachés à une vidéo est géré par VideoSheet2.tsx via
// createDocument() (backendServices/documentManagment/api.ts), qui crée d'abord le
// Document (POST /api/documents) puis uploade le fichier par chunks avec le documentId
// obtenu en retour. Il n'y a donc pas besoin d'un second mécanisme d'upload ici.

/**
 * Supprime les documents marqués pour suppression
 */
const deleteMarkedDocuments = async (
  videoId: number,
  documents: DocumentWithState[]
): Promise<void> => {
  const documentsToDelete = documents.filter(doc => doc.isMarkedForDeletion && doc.id);

  for (const doc of documentsToDelete) {
    try {
      const result = await deleteDocument(doc.id!);

      if (result.code === 1) {
        console.log(`Document ID ${doc.id} supprimé pour la vidéo ID: ${videoId}`);
      } else {
        console.error(`Erreur lors de la suppression du document ID ${doc.id}:`, result.comment);
      }
    } catch (error) {
      console.error(`Erreur lors de la suppression du document ID ${doc.id}:`, error);
    }
  }
};

// ===== FONCTIONS API VIDÉO =====

export interface BackendResponse_VideoList extends BackendResponse<Video[]> {}

// `videoId` : une seule vidéo, au format de la liste (catégories, statut requis, mots-clés,
// produits associés) que GET /api/videos/{id} ne rend pas — c'est la fiche du Dashboard qui s'en sert.
export const getVideoList = async (reload: boolean = false, adminId?: number, videoId?: number): Promise<BackendResponse_VideoList> => {
  const url = API_URLS.getVideoList;

  try
   {
    const response = await axios.get(url, { params: { ...(adminId ? { adminId } : {}), ...(videoId ? { id: videoId } : {}) } });

    if (response.status === 200) 
	      {
           const videoList: Video[] = response.data.map((video: any) => {
		  
             const defaultVideo = initializedVideo();
             const productId            = video.videoSets?.[0]?.videoSet?.products?.[0]?.product.id || 0;
             const categoriesIds        = video.mediaCategories?.map((category: any) => category.id) || [];
             const keywordsIds          = video.keywordsIds?.map((keyword: any) => keyword.id) || [];
             const associatedProductIds = video.associatedProductIds?.map((id: any) => id) || [];

             return {
               ...defaultVideo,
               id                       : video.idBis                    ?? defaultVideo.id,
               uuid                     : video.uuid                     ?? defaultVideo.uuid,
               publicRef                : video.publicRef                     ?? defaultVideo.publicRef,
               slug                     : video.slug                         ?? defaultVideo.slug,
               creationDate             : video.creationDate             ? new Date(video.creationDate.date) : defaultVideo.creationDate,
               creationIpAddress        : video.creationIpAddress        ?? defaultVideo.creationIpAddress,
               mediaType                : video.mediaType,
               name                     : video.name,
               thumbnail                : video.thumbnail,
               comment                  : video.comment,
               state                    : video.state,
               active                   : video.active                   ?? defaultVideo.active,
               activeScheduled          : video.activeScheduled          ?? defaultVideo.activeScheduled,
               activeStartDate          : video.activeStartDate          ? new Date(video.activeStartDate.date) : defaultVideo.activeStartDate,
               activeEndDate            : video.activeEndDate            ? new Date(video.activeEndDate.date) : defaultVideo.activeEndDate,
               transcoded               : video.transcoded               ?? defaultVideo.transcoded,
               publicPortal             : video.publicPortal             ?? defaultVideo.publicPortal,
               automaticPlaylist        : !!video.automaticPlaylist,
               multiview                : !!video.multiview,
               commentsModeration       : video.commentsModeration       ?? defaultVideo.commentsModeration,
               requiredUserStatusId     : video.requiredUserStatusId     ?? defaultVideo.requiredUserStatusId,
               categoriesIds            : categoriesIds,
               keywordsIds              : keywordsIds,
               associatedProductIds     : associatedProductIds,
               associatedProductId      : video.associatedProductId      ?? defaultVideo.associatedProductId,
               hasSalesCampaign         : video.hasSalesCampaign         ?? defaultVideo.hasSalesCampaign,
               visible                  : video.visible,
               title                    : video.title                    ?? defaultVideo.title,
               shortTitle               : video.shortTitle               ?? defaultVideo.shortTitle,
               complementToTheTitle     : video.complementToTheTitle     ?? defaultVideo.complementToTheTitle,
               description              : video.description,
               duration                 : video.duration                 ?? defaultVideo.duration,
               distribution             : video.distribution             ?? defaultVideo.distribution,
               production               : video.production               ?? defaultVideo.production,
               designer                 : video.designer                 ?? defaultVideo.designer,
               copyright                : video.copyright                ?? defaultVideo.copyright,
               releaseDate              : video.releaseDate              ? new Date(video.releaseDate.date) : defaultVideo.releaseDate,
               firstUseDate             : video.firstUseDate             ? new Date(video.firstUseDate.date) : defaultVideo.firstUseDate,
               ranking                  : video.ranking                  ?? defaultVideo.ranking,
               numberOfViews            : video.numberOfViews            ?? defaultVideo.numberOfViews,
               imageFile                : video.imageFile                ?? defaultVideo.imageFile,
               videoFile                : video.videoFile                ?? defaultVideo.videoFile,
               originalFile             : video.originalFile             ?? defaultVideo.originalFile,

               storageBytes             : video.storageBytes             ?? defaultVideo.storageBytes,
               storageOriginBytes       : video.storageOriginBytes       ?? 0,
               storageComputedAt        : video.storageComputedAt         ? new Date(video.storageComputedAt.date) : defaultVideo.storageComputedAt,
               originalImageFile        : video.originalImageFile        ?? defaultVideo.originalImageFile,
               youtubeId                : video.youtubeId                ?? defaultVideo.youtubeId,
               productId                : productId,
               computedDuration         : video.duration                 ? timeStringToSeconds(video.duration) : 0,
               numberOfVotes            : video.numberOfVotes            ?? defaultVideo.numberOfVotes,
               relativeVotesValue       : video.relativeVotesValue       ?? defaultVideo.relativeVotesValue,
               numberOfViewsFromYoutube : video.numberOfViewsFromYoutube ?? defaultVideo.numberOfViewsFromYoutube,
               likeCount                : video.likeCount                ?? defaultVideo.likeCount,
               dislikeCount             : video.dislikeCount             ?? defaultVideo.dislikeCount,
              };
            });

            return {
                     code    : 1,
                     comment : 'The retrieval of videos is complete.',
                     payload : videoList,
                   };
          } 
	 else {
           return handleError<Video[]>(response.status, 'Non-200 response from API');
          }
   } 
  catch (error) 
   {
    if (axios.isAxiosError(error)) 
	 {
      return handleError<Video[]>(-101, error.response?.data?.detail ?? error.message);
     }
    return handleError<Video[]>(-102, 'Unknown error');
  }
};

export interface BackendResponse_Video extends BackendResponse<Video> {}

// GET /api/videos/{id} rend les dates en chaîne ISO ("2026-10-14T14:28:00+00:00"), alors que les
// listes les rendent en { date: "…" } : lire `.date` d'une chaîne donnait une date invalide, et la
// fiche d'une vidéo ouverte depuis le Dashboard plantait au rendu ("Invalid time value").
const dateOf = (v: any, fallback: Date | null): Date | null => {
  if (!v) return fallback;
  const d = new Date(typeof v === 'string' ? v : v.date);
  return isNaN(d.getTime()) ? fallback : d;
};

export const getVideoById = async (videoId: number): Promise<BackendResponse_Video> => {
  const baseUrl = API_URLS.getVideoById;
  const url = baseUrl.replace('{id}', `${videoId}`);

  try {
    const response = await axios.get(url);

    if (response.status === 200) {
      const defaultVideo = initializedVideo();
      const productId = response.data.videoSets?.[0]?.videoSet?.products?.[0]?.product.id || 0;
      const categoriesIds = response.data.mediaCategories?.map((category: any) => category.id) || [];
      const keywordsIds = response.data.keywordsIds?.map((keyword: any) => keyword.id) || [];

      const video: Video = {
        ...defaultVideo,
        id: response.data.idBis ?? defaultVideo.id,
        creationDate: dateOf(response.data.creationDate, defaultVideo.creationDate),
        creationIpAddress: response.data.creationIpAddress ?? defaultVideo.creationIpAddress,
        uuid: response.data.uuid,
        publicRef: response.data.publicRef,
        mediaType: response.data.mediaType,
        name: response.data.name,
        thumbnail: response.data.thumbnail,
        comment: response.data.comment,
        state: response.data.state,
        active: response.data.active ?? defaultVideo.active,
        activeScheduled: response.data.activeScheduled ?? defaultVideo.activeScheduled,
        activeStartDate: dateOf(response.data.activeStartDate, defaultVideo.activeStartDate),
        activeEndDate: dateOf(response.data.activeEndDate, defaultVideo.activeEndDate),
        transcoded: response.data.transcoded ?? defaultVideo.transcoded,
        publicPortal: response.data.publicPortal ?? defaultVideo.publicPortal,
        automaticPlaylist: !!response.data.automaticPlaylist,
        commentsModeration: response.data.commentsModeration ?? defaultVideo.commentsModeration,
        requiredUserStatusId: response.data.requiredUserStatusId ?? defaultVideo.requiredUserStatusId,
        categoriesIds: categoriesIds,
        keywordsIds: keywordsIds,
        associatedProductId: response.data.associatedProductId ?? defaultVideo.associatedProductId,
        hasSalesCampaign: response.data.hasSalesCampaign ?? defaultVideo.hasSalesCampaign,
        visible: response.data.visible,
        title: response.data.title ?? defaultVideo.title,
        shortTitle: response.data.shortTitle ?? defaultVideo.shortTitle,
        complementToTheTitle: response.data.complementToTheTitle ?? defaultVideo.complementToTheTitle,
        description: response.data.description,
        duration: response.data.duration ?? defaultVideo.duration,
        distribution: response.data.distribution ?? defaultVideo.distribution,
        production: response.data.production ?? defaultVideo.production,
        designer: response.data.designer ?? defaultVideo.designer,
        copyright: response.data.copyright ?? defaultVideo.copyright,
        releaseDate: dateOf(response.data.releaseDate, defaultVideo.releaseDate),
        firstUseDate: dateOf(response.data.firstUseDate, defaultVideo.firstUseDate),
        ranking: response.data.ranking ?? defaultVideo.ranking,
        numberOfViews: response.data.numberOfViews ?? defaultVideo.numberOfViews,
        imageFile: response.data.imageFile ?? defaultVideo.imageFile,
        videoFile: response.data.videoFile ?? defaultVideo.videoFile,
        originalFile: response.data.originalFile ?? defaultVideo.originalFile,
        originalImageFile: response.data.originalImageFile ?? defaultVideo.originalImageFile,
        youtubeId: response.data.youtubeId ?? defaultVideo.youtubeId,
        productId: productId,
        computedDuration: response.data.duration ? timeStringToSeconds(response.data.duration) : 0,
        numberOfVotes: response.data.numberOfVotes ?? defaultVideo.numberOfVotes,
        relativeVotesValue: response.data.relativeVotesValue ?? defaultVideo.relativeVotesValue,
        numberOfViewsFromYoutube: response.data.numberOfViewsFromYoutube ?? defaultVideo.numberOfViewsFromYoutube,
        likeCount: response.data.likeCount ?? defaultVideo.likeCount,
        dislikeCount: response.data.dislikeCount ?? defaultVideo.dislikeCount,
      };

      return {
        code: 1,
        comment: 'The retrieval of video is complete.',
        payload: video,
      };
    } else {
      return handleError<Video>(response.status, 'Non-200 response from API');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<Video>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<Video>(-102, 'Unknown error');
  }
};

export const updateVideoVisibility = async (id: number, state: number): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.patchVideoVisibility}`;
  const url = baseUrl.replace('{id}', `${id}`);
  const visibilityStr = state === 1 ? 'true' : 'false';

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();
  
  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'application/merge-patch+json',
	'User-Agent-Informations': `${userAgentInformations}`,
	               'App-Informations': `${appInformations}`,
  };

  try {
    const response = await axios.patch(url, { visibilityStr }, { headers });

    if (response.status === 200 || response.status === 204) {
      return {
        code: 1,
        comment: 'Video visibility updated successfully.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Non-200/204 response from API');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Network error');
  }
};

export const deleteVideo = async ( id       : number ,
                                   adminId? : number ): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.deleteVideo}`;
  const url = baseUrl.replace('{id}', `${id}`);

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();

  const headers = {
    'Accept': 'application/ld+json',
	'User-Agent-Informations': `${userAgentInformations}`,
	'App-Informations': `${appInformations}`,
    'X-Admin-Id': adminId ?? ''
  };

  console.log('[deleteVideo] appel', { url, id, adminId, headers });

  try {
    const response = await axios.delete(url, { headers });

    console.log('[deleteVideo] réponse OK', { status: response.status, data: response.data });

    if (response.status === 204) {
      return {
        code: 1,
        comment: 'The video has been successfully deleted.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error deleting the video');
    }
  } catch (error) {
    console.log('[deleteVideo] erreur brute', error);

    if (axios.isAxiosError(error)) {
      console.log('[deleteVideo] axios error détail', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        responseData: error.response?.data,
        requestSent: !!error.request,
      });

      if (error.response?.status === 409) {
        return {
          code: -103,
          // Cf. api_audioSlideshow : la phrase du serveur nomme la chaîne et l'horaire.
          comment: extractServerConflictMessage(error.response?.data),
          payload: {} as any
        };
      }
      if (error.response?.status === 404) {
        return {
          code: -104,
          comment: 'The video no longer exists.',
          payload: {} as any
        };
      }
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

export const retryVideoTranscoding = async ( id       : number ,
                                             adminId? : number ): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.retryVideoTranscoding}`;
  const url = baseUrl.replace('{id}', `${id}`);

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();

  const headers = {
    'Accept': 'application/ld+json',
	'User-Agent-Informations': `${userAgentInformations}`,
	'App-Informations': `${appInformations}`,
    'X-Admin-Id': adminId ?? ''
  };

  try {
    const response = await axios.post(url, {}, { headers });

    if (response.status === 204) {
      return {
        code: 1,
        comment: 'Le transcodage a été relancé.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error retrying the video transcoding');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

export const cancelVideoTranscoding = async ( id       : number ,
                                              adminId? : number ): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.cancelVideoTranscoding}`;
  const url = baseUrl.replace('{id}', `${id}`);

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();

  const headers = {
    'Accept': 'application/ld+json',
	'User-Agent-Informations': `${userAgentInformations}`,
	'App-Informations': `${appInformations}`,
    'X-Admin-Id': adminId ?? ''
  };

  try {
    const response = await axios.post(url, {}, { headers });

    if (response.status === 204) {
      return {
        code: 1,
        comment: 'Le transcodage en échec a été annulé.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error cancelling the video transcoding');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

export interface VideoAudioTrack {
  index    : number;        // position parmi les pistes audio, PAS l'index de stream global — convention -map 0:a:<index> côté ffmpeg-microservice
  language : string | null; // tag ISO 639-2 tel que renvoyé par ffprobe (ex. "fre", "eng"), ou null si absent
  label    : string;        // libellé prêt à afficher (langue traduite, ou tag brut, ou "Piste N")
  codec    : string | null;
  channels : number | null;
}

export interface BackendResponse_VideoAudioTracks extends BackendResponse<VideoAudioTrack[]> {}

// Liste les pistes audio déjà présentes dans le rendition HLS transcodée d'une vidéo
// (cf. VideoAudioTracks côté admin-api). Utilisé à la programmation dans le programme
// du jour pour proposer un choix de langue quand une vidéo en a plusieurs.
export const getVideoAudioTracks = async (id: number): Promise<BackendResponse_VideoAudioTracks> => {
  const baseUrl = `${API_URLS.getVideoAudioTracks}`;
  const url = baseUrl.replace('{id}', `${id}`);

  try {
    const response = await axios.get(url);

    if (response.status === 200) {
      return {
        code: 1,
        comment: 'Pistes audio récupérées',
        payload: response.data?.tracks ?? [],
      };
    }
    return handleError<VideoAudioTrack[]>(response.status, 'Erreur lors de la récupération des pistes audio');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // 404 = vidéo non transcodée ou introuvable : cas normal, pas une panne — l'appelant
      // doit juste se comporter comme si la vidéo n'avait qu'une seule piste (pas de choix).
      if (error.response?.status === 404) {
        return { code: 1, comment: 'Aucune piste détectée', payload: [] };
      }
      return handleError<VideoAudioTrack[]>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<VideoAudioTrack[]>(-102, 'Unknown error');
  }
};

export interface VideoPlayability {
  playable : boolean;
  reason   : string | null;
}

// Cette vidéo peut-elle réellement être diffusée ? Le fichier peut exister et être illisible
// (piste audio déclarée à 0 canal : ffmpeg n'écrit rien et l'antenne reste vide toute la durée
// du programme). Interroge le même endpoint que getVideoAudioTracks, qui applique la règle
// serveur (MediaPlayabilityChecker) — c'est admin-api qui fait autorité, et il REFUSE de toute
// façon la programmation d'un tel média : ceci n'est que le retour immédiat, dès le choix.
//
// Ne conclut à « illisible » que sur une réponse explicite : erreur réseau, 404 (vidéo non
// transcodée), réponse sans le champ → on laisse passer, le serveur tranchera à la programmation.
export const getVideoPlayability = async (id: number): Promise<VideoPlayability> => {
  const url = `${API_URLS.getVideoAudioTracks}`.replace('{id}', `${id}`);

  try {
    const response = await axios.get(url);

    if (response.status === 200 && response.data?.playable === false) {
      return { playable: false, reason: response.data?.unplayableReason ?? 'le fichier vidéo est illisible' };
    }
  } catch {
    // voir ci-dessus : dans le doute, on laisse passer
  }

  return { playable: true, reason: null };
};

export const createVideoMetadata = async (
  title: string,
  shortTitle: string,
  complementToTheTitle: string,
  description: string,
  duration: string,
  distribution: string,
  production: string,
  copyright: string,
  releaseDate: Date | null,
  firstUseDate: Date | null,
  rankingStr: string,
  numberOfViewsStr: string,
  imageUrl: string,
  imageFileToUpload: File | null,
  youtubeId: string,
  requiredUserStatusId: number,
  productId: number,
  categoriesIds: number[],
  keywordsIds: number[],
  documents: DocumentWithState[],
  publicPortal: boolean,
  commentsModeration: number,
  active: boolean     ,
  activeScheduled: boolean     ,
  activeStartDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  activeEndDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  adminId?     : number,
  designer     : string = '',
  /**
   * Réponse à la question posée au clic sur « Créer » quand la vidéo n'est pas
   * active : faut-il l'activer une fois son transcodage terminé ? Intention à
   * usage unique, consommée par l'API à la fin du transcodage.
   */
  activateAfterTranscoding: boolean = false,
  /**
   * Même principe qu'activateAfterTranscoding : réseaux sociaux cochés dans le
   * bloc « Publier sur les réseaux » avant même que la vidéo n'existe. Le mp4
   * n'est jamais renvoyé au navigateur : c'est à l'API de déclencher la
   * publication une fois SON transcodage terminé, avec le fichier qu'elle a déjà.
   * Intention à usage unique, consommée côté serveur.
   */
  socialNetworksToPublishAfterTranscoding: { code: string; privacyStatus: string }[] = [],
  automaticPlaylist?: boolean
): Promise<BackendResponse> => {
  const url = API_URLS.createVideo;

  const formData = new FormData();
  formData.append("title", title);
  formData.append("shortTitle", shortTitle);
  formData.append("complementToTheTitle", complementToTheTitle);
  formData.append("description", description);
  formData.append("duration", duration);
  formData.append("distribution", distribution);
  formData.append("production", production);
  formData.append("designer", designer);
  formData.append("copyright", copyright);
  formData.append("releaseDateStr", convertDateToString(releaseDate));
  formData.append("firstUseDateStr", convertDateToString(firstUseDate));
  formData.append("rankingStr", rankingStr);
  formData.append("numberOfViewsStr", numberOfViewsStr);
  formData.append("imageUrl", imageUrl);
  if (imageFileToUpload) formData.append("imageFileToUpload", imageFileToUpload);
  formData.append("youtubeId", youtubeId);
  formData.append("requiredUserStatusIdStr", requiredUserStatusId.toString());
  formData.append("productIdStr", productId.toString());
  formData.append("categoriesIdsStr", categoriesIds.join(','));
  formData.append("keywordsIdsStr", keywordsIds.join(','));
  formData.append('publicPortal', publicPortal.toString());
  if (automaticPlaylist !== undefined) formData.append('automaticPlaylist', automaticPlaylist.toString());
  formData.append('commentsModeration', commentsModeration.toString());
  formData.append('active', active.toString());
  formData.append('activateAfterTranscoding', activateAfterTranscoding.toString());
  if (socialNetworksToPublishAfterTranscoding.length > 0) {
    formData.append('socialNetworksToPublishAfterTranscodingStr', JSON.stringify(socialNetworksToPublishAfterTranscoding));
  }
  formData.append('activeScheduled', activeScheduled.toString());
  if ( activeStartDate ) { formData.append('activeStartDateStr', activeStartDate.toString()); }
  if ( activeEndDate) { formData.append('activeEndDateStr', activeEndDate.toString()); }
  if (adminId)                                 formData.append ( 'adminId'                         , adminId.toString()               );

console.log ('createVideoMetadata activeStartDate=', activeStartDate)
console.log ('createVideoMetadata activeEndDate=', activeEndDate)


  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();
  
  try {
    const response = await axios.post(url, formData, {
      headers: { Accept: 'application/ld+json' ,'User-Agent-Informations': `${userAgentInformations}`,
	               'App-Informations': `${appInformations}`,}
    });

    if (response.status === 201) {
      const videoId = response.data.id || response.data.idBis;
      return {
        code: 1,
        comment: 'Video metadata created successfully.',
        payload: videoId
      };
    } else {
      return handleError<void>(response.status, 'Error creating video metadata');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

export const createVideo = async (
  title: string,
  shortTitle: string,
  complementToTheTitle: string,
  description: string,
  duration: string,
  distribution: string,
  production: string,
  copyright: string,
  releaseDate: Date | null,
  firstUseDate: Date | null,
  rankingStr: string,
  numberOfViewsStr: string,
  imageUrl: string,
  imageFileToUpload: File | null,
  videoFileToUpload: File | null,
  youtubeId: string,
  requiredUserStatusId: number,
  productId: number,
  categoriesIds: number[],
  keywordsIds: number[],
  publicPortal: boolean,
  commentsModeration: number,
  active: boolean     ,
  activeScheduled: boolean     ,
  activeStartDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  activeEndDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  documents: DocumentWithState[],
  setProgress?: ((progress: number) => void) | null,
  adminId?     : number,
  designer     : string = '',
  resolutions  : string[] = [],
  activateAfterTranscoding: boolean = false,
  /**
   * Source = un enregistrement de régie déjà sur le disque du serveur, plutôt
   * qu'un fichier du navigateur. Incompatible avec videoFileToUpload — passé
   * par VideoSheet2 uniquement quand aucun fichier n'a été choisi (voir
   * prefillFromRecording). Zéro upload : la fiche est immédiatement envoyée
   * en transcodage sur le chemin déjà connu de la session d'enregistrement.
   */
  recordingSource?: { channelUuid: string; sessionId: number; publishAngles?: boolean } | null,
  /**
   * Même principe qu'activateAfterTranscoding : réseaux sociaux cochés avant
   * même que la vidéo n'existe, à publier une fois le transcodage terminé.
   */
  socialNetworksToPublishAfterTranscoding: { code: string; privacyStatus: string }[] = [],
  automaticPlaylist?: boolean,
  /** Réglage de compression 0-3 (0 sans perte, 1 légère, 2 compromis, 3 forte) ; le serveur en déduit le CRF. */
  compression?: number
): Promise<BackendResponse> => {

console.log ('createVideo activeStartDate=', activeStartDate)
console.log ('createVideo activeEndDate=', activeEndDate)

  try {
    // 1. Créer d'abord les métadonnées de la vidéo
    const metadataResult = await createVideoMetadata(
      title, shortTitle, complementToTheTitle, description, duration,
      distribution, production, copyright, releaseDate, firstUseDate,
      rankingStr, numberOfViewsStr, imageUrl, imageFileToUpload,
      youtubeId, requiredUserStatusId, productId, categoriesIds, keywordsIds, documents, publicPortal, commentsModeration,
	  active, activeScheduled, activeStartDate, activeEndDate, adminId, designer, activateAfterTranscoding,
	  socialNetworksToPublishAfterTranscoding, automaticPlaylist
    );

    if (metadataResult.code !== 1) {
      return metadataResult;
    }

    const videoId = metadataResult.payload;

    // 2. Source du fichier vidéo : upload navigateur, OU enregistrement déjà sur
    // le serveur — jamais les deux à la fois.
    if (videoFileToUpload) {
      const uploadConfig = {
        uploadChunkUrl: API_URLS.uploadVideoChunk, // API_URLS.uploadMediaChunk,
        finalizeUploadUrl: API_URLS.finalizeVideoUpload,
        chunkSize: 10 * 1024 * 1024, // 10MB
        maxRetries: 3,
        enableResume: true,
        entityId: videoId,
        entityType: 'video',
        autoStart: true,
        metadata: {
          videoId: videoId,
          mediaType: 'video',
          ...(resolutions.length > 0 ? { resolutions: resolutions.join(',') } : {}),
          ...(compression !== undefined ? { compression } : {})
        }
      };

      const uploader = createUpload(videoFileToUpload, uploadConfig);
      console.log(`Upload vidéo démarré en arrière-plan pour la vidéo ID: ${videoId}, Upload ID: ${uploader.id}`);
    } else if (recordingSource) {
      const result = await axios.post(API_URLS.finalizeVideoFromRecording, {
        videoId,
        channelUuid       : recordingSource.channelUuid,
        recordingSessionId: recordingSource.sessionId,
        resolutions       : resolutions.join(','),
        ...(compression !== undefined ? { compression } : {}),
      });
      console.log(`Vidéo ID ${videoId} liée à l'enregistrement #${recordingSource.sessionId}, transcodage démarré.`, result.data);

      // Prise multi-angle : les autres fichiers de la prise deviennent les angles (vidéos enfants
      // invisibles) de cette vidéo. Un échec ici ne défait pas la vidéo principale, déjà en transcodage.
      if (recordingSource.publishAngles) {
        try {
          const angles = await axios.post(API_URLS.finalizeMultiviewAngles, {
            videoId,
            channelUuid       : recordingSource.channelUuid,
            recordingSessionId: recordingSource.sessionId,
            resolutions       : resolutions.join(','),
            ...(compression !== undefined ? { compression } : {}),
          });
          console.log(`Angles de la vidéo ID ${videoId} publiés.`, angles.data);
        } catch (anglesError) {
          console.error(`Publication des angles de la vidéo ID ${videoId} échouée`, anglesError);
        }
      }
    }

    // 3. Retourner immédiatement le résultat (non-bloquant)
    // (l'upload des nouveaux documents est géré par l'appelant via createDocument(), voir plus haut)
    const hasUploads = videoFileToUpload || recordingSource || documents.some(doc => doc.isNew && doc.fileToUpload);
    return {
      code: 1,
      comment: hasUploads
        ? 'Video created successfully. Uploads started in background.'
        : 'Video created successfully.',
      payload: videoId
    };

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error creating video');
  }
};

export const updateVideoMetadata = async (
  id: number,
  title: string,
  shortTitle: string,
  complementToTheTitle: string,
  description: string,
  duration: string,
  distribution: string,
  production: string,
  copyright: string,
  releaseDate: Date | null,
  firstUseDate: Date | null,
  rankingStr: string,
  numberOfViewsStr: string,
  imageUrl: string,
  imageFileToUpload: File | null,
  videoFileToUpload: File | null,
  youtubeId: string,
  requiredUserStatusId: number,
  productId: number,
  categoriesIds: number[],
  keywordsIds: number[],
  documents: DocumentWithState[],
  publicPortal: boolean,
  commentsModeration: number,
  active: boolean     ,
  activeScheduled: boolean     ,
  activeStartDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  activeEndDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  adminId?     : number,
  designer     : string = '',
  automaticPlaylist?: boolean

): Promise<BackendResponse> => {

  const productIdStr = productId.toString();
  const categoriesIdsStr = categoriesIds.join(',');
  const keywordsIdsStr = keywordsIds.join(',');

  const baseUrl = `${API_URLS.patchVideo}`;
  const url = baseUrl;

  const formData = new FormData();
  formData.append('id', id.toString());
  formData.append('title', title);
  formData.append('shortTitle', shortTitle);
  formData.append('complementToTheTitle', complementToTheTitle);
  formData.append('description', description);
  formData.append('duration', duration);
  formData.append('distribution', distribution);
  formData.append('production', production);
  formData.append('designer', designer);
  formData.append('copyright', copyright);
  formData.append('releaseDateStr', convertDateToString(releaseDate));
  formData.append('firstUseDateStr', convertDateToString(firstUseDate));
  formData.append('rankingStr', rankingStr);
  formData.append('numberOfViewsStr', numberOfViewsStr);

  if (imageFileToUpload !== null) {
    formData.append('imageFileToUpload', imageFileToUpload);
  }
  if (videoFileToUpload !== null) {
    formData.append('videoFileIsUpload', 'true');
  }

  formData.append('youtubeId', youtubeId);
  formData.append('requiredUserStatusIdStr', requiredUserStatusId.toString());
  formData.append('productIdStr', productIdStr);
  formData.append('categoriesIdsStr', categoriesIdsStr);
  formData.append('keywordsIdsStr', keywordsIdsStr);
  formData.append('publicPortal', publicPortal.toString());
  if (automaticPlaylist !== undefined) formData.append('automaticPlaylist', automaticPlaylist.toString());
  formData.append('commentsModeration', commentsModeration.toString());

  formData.append('active', active.toString());
  formData.append('activeScheduled', activeScheduled.toString());
  if ( activeStartDate ) { formData.append('activeStartDateStr', activeStartDate.toString()); }
  if ( activeEndDate) { formData.append('activeEndDateStr', activeEndDate.toString()); }
  if (adminId)                                 formData.append ( 'adminId'                         , adminId.toString()               );

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();
  
  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'multipart/form-data',
	'User-Agent-Informations': `${userAgentInformations}`,
	               'App-Informations': `${appInformations}`,
  };

  try {
    const response = await axios.post(url, formData, { headers });

    if (response.status === 200 || response.status === 204 || response.status === 201) {
      return {
        code: 1,
        comment: 'Video metadata updated successfully.',
        payload: id
      };
    } else {
      return handleError<void>(response.status, 'Non-200/201/204 response from API');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

export const updateVideo = async (
  id: number,
  title: string,
  shortTitle: string,
  complementToTheTitle: string,
  description: string,
  duration: string,
  distribution: string,
  production: string,
  copyright: string,
  releaseDate: Date | null,
  firstUseDate: Date | null,
  rankingStr: string,
  numberOfViewsStr: string,
  imageUrl: string,
  imageFileToUpload: File | null,
  videoFileToUpload: File | null,
  youtubeId: string,
  requiredUserStatusId: number,
  productId: number,
  categoriesIds: number[],
  keywordsIds: number[],
  publicPortal: boolean,
  commentsModeration: number,
  active: boolean     ,
  activeScheduled: boolean     ,
  activeStartDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  activeEndDate: Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  documents: DocumentWithState[],
  setProgress?: ((progress: number) => void) | null,
  adminId?     : number,
  designer     : string = '',
  resolutions  : string[] = [],
  automaticPlaylist?: boolean,
  compression?: number
): Promise<BackendResponse> => {

  try {
    // 1. Mettre à jour d'abord les métadonnées de la vidéo (sans le fichier vidéo)
    const metadataResult = await updateVideoMetadata(
      id, title, shortTitle, complementToTheTitle, description, duration,
      distribution, production, copyright, releaseDate, firstUseDate,
      rankingStr, numberOfViewsStr, imageUrl, imageFileToUpload, videoFileToUpload,
      youtubeId, requiredUserStatusId, productId, categoriesIds, keywordsIds, documents, publicPortal, commentsModeration,
	  active, activeScheduled, activeStartDate, activeEndDate, adminId, designer, automaticPlaylist
    );

    if (metadataResult.code !== 1) {
      return metadataResult;
    }

    // 2. Si un nouveau fichier vidéo est fourni, lancer l'upload en arrière-plan par morceaux
    if (videoFileToUpload !== null) {
      const uploadConfig = {
        uploadChunkUrl: API_URLS.uploadVideoChunk, // API_URLS.uploadMediaChunk,
        finalizeUploadUrl: API_URLS.finalizeVideoUpload,
        chunkSize: 10 * 1024 * 1024, // 10MB
        maxRetries: 3,
        enableResume: true,
        entityId: id,
        entityType: 'video',
        autoStart: true,
        metadata: {
          videoId: id,
          mediaType: 'video',
          isUpdate: true,
          ...(resolutions.length > 0 ? { resolutions: resolutions.join(',') } : {}),
          ...(compression !== undefined ? { compression } : {})
        }
      };

      const uploader = createUpload(videoFileToUpload, uploadConfig);
      console.log(`Upload vidéo (mise à jour) démarré en arrière-plan pour la vidéo ID: ${id}, Upload ID: ${uploader.id}`);
    }

    // 3. Supprimer les documents marqués pour suppression
    // (l'upload des nouveaux documents est géré par l'appelant via createDocument(), voir plus haut)
    if (documents && documents.length > 0) {
      await deleteMarkedDocuments(id, documents);
    }

    // 4. Retourner immédiatement le résultat (non-bloquant)
    const hasUploads = videoFileToUpload || documents.some(doc => doc.isNew && doc.fileToUpload);
    return {
      code: 1,
      comment: hasUploads
        ? 'Video updated successfully. Uploads started in background.'
        : 'Video updated successfully.',
      payload: id
    };

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error updating video');
  }
};


// Désactive une vidéo et la retire du portail Kleon.tv (enabled=false), ou la rétablit
// (enabled=true, remise sur le portail seulement si `portal`). Rien n'est supprimé.
/** Met une vidéo à la une du portail jusqu'à `until` (date ISO), ou l'en retire (null). Réservé aux superadmins. */
export const setVideoFeatured = async ( adminId : number ,
                                        videoId : number ,
                                        until   : string | null ): Promise<{ ok: boolean; error: string }> => {
  try {
    await axios.post(API_URLS.setVideoFeatured, { adminId, videoId, until });
    return { ok: true, error: '' };
  } catch (error) {
    const message = axios.isAxiosError(error) ? (error.response?.data?.error ?? error.message) : 'Erreur inconnue';
    return { ok: false, error: String(message) };
  }
};

export const setVideoAvailability = async ( adminId : number  ,
                                            videoId : number  ,
                                            enabled : boolean ,
                                            portal  : boolean = false ,
                                            comment : string  = ''    ): Promise<{ ok: boolean; error: string; email?: { sent: boolean; to: string | null; error: string | null } | null }> => {
  try {
    const { data } = await axios.post(API_URLS.setVideoAvailability, { adminId, videoId, enabled, portal, comment });
    return { ok: true, error: '', email: data?.email ?? null };
  } catch (error) {
    const message = axios.isAxiosError(error) ? (error.response?.data?.error ?? error.message) : 'Erreur inconnue';
    return { ok: false, error: String(message) };
  }
};
